import qs, { type StringifiableRecord } from 'query-string';
import { isEmpty, isNonNullish, isNullish, isObjectType, isString, omitBy } from 'remeda';

export type SortOrder = 'asc' | 'desc';

// Known fields are typed; the open tail holds the dynamic per-view keys from the URL.
export type FilterValues = {
  page?: number;
  page_limit?: number;
  sort_by?: string;
  sort_order?: SortOrder;
  quick_search?: string;
} & Record<string, unknown>;

const isDroppable = (value: unknown): boolean => {
  if (isNullish(value)) return true;
  if (isString(value)) return isEmpty(value);
  if (isObjectType(value)) return isEmpty(value);

  return true;
};

const toNumber = (value: unknown): number | undefined => {
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const coerceFilterTypes = (filters: Record<string, unknown>): FilterValues => {
  const coerced: FilterValues = { ...filters };
  if ('page' in filters) coerced.page = toNumber(filters.page);
  if ('page_limit' in filters) coerced.page_limit = toNumber(filters.page_limit);
  return coerced;
};

export const normalizeFilters = (
  values: FilterValues,
  currentFilters: FilterValues,
): FilterValues => ({
  ...omitBy(values, isDroppable),
  page: 1,
  page_limit: values.page_limit || currentFilters.page_limit,
});

// Returns null for the non-sortable `actions` column so the caller can no-op.
export const toggleSort = (filters: FilterValues, field: string): FilterValues | null => {
  if (field === 'actions') return null;

  if (filters.sort_by !== field) return { ...filters, sort_by: field, sort_order: 'asc' };

  return { ...filters, sort_order: filters.sort_order === 'asc' ? 'desc' : 'asc' };
};

export const serializeFiltersToSearch = (filters: FilterValues): string => {
  const serialized = { ...filters };

  // The backend expects last_reminder_step_type as a `[a,b]` literal, not the
  // plain comma form the other array filters get.
  if (isNonNullish(filters.last_reminder_step_type)) {
    serialized.last_reminder_step_type = `[${String(filters.last_reminder_step_type)}]`;
  }

  // query-string only accepts stringifiable values; filters is an untyped URL bag.
  return qs.stringify(serialized as StringifiableRecord, { arrayFormat: 'comma' });
};

export const removeFilterKeys = (
  filters: FilterValues,
  key: string,
  extraKey?: string,
): FilterValues => ({
  ...filters,
  [key]: undefined,
  ...(extraKey ? { [extraKey]: undefined } : {}),
});
